import type {
  BoardingPassParseResult,
  BoardingPassSegment,
  Flight,
  FlightLookupResult,
  FlightsFilter,
  FlightsMapResponse,
} from '../../types/flight';
import { awaitJob, fetchAsyncJob } from './asyncJob';
import { getGroundTripTags } from './groundTripsApi';
import { getHotelTags } from './hotelsApi';
import { API_URL, fetchApi, fetchWithAuth } from './client';

export type { BoardingPassParseResult, BoardingPassSegment, FlightsFilter };

/**
 * Look up flight by number and date for auto-fill.
 */
export const lookupFlight = async (
  flightNumber: string,
  date: string,
  lang?: string
): Promise<FlightLookupResult | null> => {
  try {
    const params = new URLSearchParams({ flightNumber, date });
    if (lang) params.set('lang', lang);
    return await fetchApi<FlightLookupResult>(`${API_URL}/flights/lookup?${params}`);
  } catch {
    return null;
  }
};

/** Parse boarding pass / itinerary (image or PDF) via backend LLM (async job). */
export const parseBoardingPass = async (
  file: File,
  lang?: string
): Promise<BoardingPassParseResult> => {
  const params = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  const body = new FormData();
  body.append('file', file);
  const response = await fetchWithAuth(`${API_URL}/flights/parse-boarding-pass${params}`, {
    method: 'POST',
    body,
  });
  if (response.status === 401) {
    throw new Error('Session expired');
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`);
  }
  const accepted = (await response.json()) as { jobId?: string };
  if (!accepted?.jobId) {
    throw new Error('No jobId in response');
  }
  return awaitJob<BoardingPassParseResult>(accepted.jobId, { timeoutMs: 180_000 });
};

/**
 * Get distinct flight tags for the current user (for filter UI).
 */
export const getFlightTags = async (): Promise<{ tags: string[] }> => {
  return fetchApi<{ tags: string[] }>(`${API_URL}/flights/tags`);
};

/**
 * Get flights with airport coordinates for map visualization.
 */
export async function getFlightsMap(lang: string = 'en'): Promise<FlightsMapResponse> {
  const params = new URLSearchParams({ lang });
  return fetchApi<FlightsMapResponse>(`${API_URL}/flights/map?${params}`);
}

export const getCarTripTags = async (): Promise<{ tags: string[] }> => {
  return fetchApi<{ tags: string[] }>(`${API_URL}/car-trips/tags`);
};

export const getAllTripTags = async (): Promise<string[]> => {
  const [flights, cars, buses, trains, ferries, hotels] = await Promise.all([
    getFlightTags().catch(() => ({ tags: [] as string[] })),
    getCarTripTags().catch(() => ({ tags: [] as string[] })),
    getGroundTripTags('bus').catch(() => ({ tags: [] as string[] })),
    getGroundTripTags('train').catch(() => ({ tags: [] as string[] })),
    getGroundTripTags('ferry').catch(() => ({ tags: [] as string[] })),
    getHotelTags().catch(() => ({ tags: [] as string[] })),
  ]);
  return Array.from(
    new Set(
      [
        ...(flights.tags || []),
        ...(cars.tags || []),
        ...(buses.tags || []),
        ...(trains.tags || []),
        ...(ferries.tags || []),
        ...(hotels.tags || []),
      ]
        .map(t => t.trim())
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};

/**
 * Get all flights with pagination and optional filters.
 */
export const getFlights = async (
  lang?: string,
  page: number = 1,
  perPage: number = 10,
  filter?: FlightsFilter | null
): Promise<{ total: number; total_distance: number; total_price: number; items: Flight[] }> => {
  const params = new URLSearchParams();
  if (lang) params.set('lang', lang);
  params.set('page', String(page));
  params.set('per_page', String(perPage));
  if (filter) {
    if (filter.q != null && filter.q.trim() !== '') params.set('q', filter.q.trim());
    if (filter.tags?.length) filter.tags.forEach(t => params.append('tag', t));
    if (filter.priceMin != null && filter.priceMin > 0) params.set('price_min', String(filter.priceMin));
    if (filter.priceMax != null && filter.priceMax > 0) params.set('price_max', String(filter.priceMax));
    if (filter.dateFrom?.trim()) params.set('date_from', filter.dateFrom.trim());
    if (filter.dateTo?.trim()) params.set('date_to', filter.dateTo.trim());
    if (filter.isInternational === true) params.set('is_international', 'true');
    if (filter.isInternational === false) params.set('is_international', 'false');
  }
  return fetchApi<{ total: number; total_distance: number; total_price: number; items: Flight[] }>(`${API_URL}/flights?${params}`);
};

/**
 * Get a flight by ID
 */
export const getFlightById = async (id: number, lang?: string): Promise<Flight> => {
  const params = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  return fetchApi<Flight>(`${API_URL}/flights/${id}${params}`);
};

/**
 * Get airport description from LLM (for info popover)
 */
export const getAirportInfo = async (iata: string, name?: string, lang?: string): Promise<{ description?: string | null }> => {
  try {
    const params = new URLSearchParams({ iata });
    if (name) params.set('name', name);
    if (lang) params.set('lang', lang);
    return await fetchAsyncJob<{ description?: string | null }>(
      `${API_URL}/flights/airport-info?${params}`,
      { method: 'POST' },
      { timeoutMs: 90_000 },
    );
  } catch {
    return {};
  }
};

/**
 * Create a new flight
 */
export const createFlight = async (flight: Omit<Flight, 'id'> & { airline_iata?: string }, lang?: string): Promise<Flight> => {
  const url = lang ? `${API_URL}/flights?lang=${encodeURIComponent(lang)}` : `${API_URL}/flights`;
  return fetchApi<Flight>(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(flight)
  });
};

/**
 * Update a flight
 */
export const updateFlight = async (id: number, flight: Partial<Flight> & { airline_iata?: string }, lang?: string): Promise<Flight> => {
  const base = `${API_URL}/flights/${id}`;
  const url = lang ? `${base}?lang=${encodeURIComponent(lang)}` : base;
  return fetchApi<Flight>(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(flight)
  });
};

/**
 * Delete a flight
 */
export const deleteFlight = async (id: number): Promise<void> => {
  await fetchApi<void>(`${API_URL}/flights/${id}`, {
    method: 'DELETE'
  });
};
